import { useRef } from 'react';
import { useFillBar } from '@v2/lib/motion';

export function ContainerIcon({ className = 'h-6 w-10' }: { className?: string }) {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 40" className={className} fill="currentColor">
            <rect x="2" y="6" width="60" height="28" rx="2" />
            <line x1="14" y1="6" x2="14" y2="34" stroke="white" strokeWidth="1.5" />
            <line x1="26" y1="6" x2="26" y2="34" stroke="white" strokeWidth="1.5" />
            <line x1="38" y1="6" x2="38" y2="34" stroke="white" strokeWidth="1.5" />
            <line x1="50" y1="6" x2="50" y2="34" stroke="white" strokeWidth="1.5" />
        </svg>
    );
}

export const CONTAINER_TONES = {
    exceed: { box: 'text-red-600 bg-red-50 ring-red-200', bar: 'bg-red-500' },
    full: { box: 'text-emerald-600 bg-emerald-50 ring-emerald-200', bar: 'bg-emerald-500' },
    fullBlue: { box: 'text-blue-600 bg-blue-50 ring-blue-200', bar: 'bg-blue-500' },
    partial: { box: 'text-amber-500 bg-amber-50 ring-amber-200', bar: 'bg-amber-400' },
    empty: { box: 'text-gray-300 bg-gray-50 ring-gray-200', bar: 'bg-gray-300' },
} as const;

export type ContainerTone = keyof typeof CONTAINER_TONES;

const fmtM3 = (n: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** Um container com a barra de ocupação animada. Marcado para a cascata de useStaggerIn. */
export function ContainerSlot({
    n,
    slotCbm,
    cbmPer,
    tone,
}: {
    n: number;
    slotCbm: number;
    cbmPer: number;
    tone: ContainerTone;
}) {
    const barRef = useRef<HTMLDivElement>(null);
    const percent = cbmPer > 0 ? (slotCbm / cbmPer) * 100 : 0;
    useFillBar(barRef, percent);

    return (
        <div
            data-motion-item
            className={
                'relative flex min-h-[4.75rem] flex-col items-center justify-center overflow-hidden rounded-lg px-1 py-2 ring-1 transition-colors duration-500 ' +
                CONTAINER_TONES[tone].box
            }
            title={
                slotCbm > 0.0001
                    ? `Container #${n}: ${fmtM3(slotCbm)} m³ de ${cbmPer} m³ (${Math.round(percent)}%)`
                    : `Container #${n}`
            }
        >
            <ContainerIcon className="h-5 w-8 sm:h-6 sm:w-10" />
            <span className="mt-1 text-[10px] font-bold opacity-70">#{n}</span>
            {slotCbm > 0.0001 && (
                <span className="mt-0.5 text-[10px] font-semibold tabular-nums leading-none">{fmtM3(slotCbm)} m³</span>
            )}
            <div className="absolute inset-x-0 bottom-0 h-1 bg-black/5">
                <div ref={barRef} className={'h-full ' + CONTAINER_TONES[tone].bar} style={{ width: 0 }} />
            </div>
        </div>
    );
}
